import { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  GripVertical,
  Plus,
  Trash2,
  SlidersHorizontal,
  RefreshCw,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getFilterFacets, updateFilterFacets, getCatalogFacets, getAdminCategories, updateCategoryExclusions, getMainCategories, updateMainCategories, type MainCategory } from '../../utils/api';
import type { FilterFacetConfig, FacetUIType, Facets, CollectionCategory } from '../../utils/types';
import { useAdminLang } from '../AdminLangContext';

interface AdminContext {
  secret: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PRODUCT_FIELDS: Record<string, { label: string; suggestedUiType: FacetUIType }> = {
  category: { label: 'Category', suggestedUiType: 'checkbox' },
  type: { label: 'Wine Color', suggestedUiType: 'color' },
  producer: { label: 'Brand', suggestedUiType: 'checkbox' },
  region: { label: 'Region', suggestedUiType: 'checkbox' },
  country: { label: 'Country', suggestedUiType: 'checkbox' },
  grape: { label: 'Grape Variety', suggestedUiType: 'checkbox' },
  price: { label: 'Price Range', suggestedUiType: 'slider' },
  rating: { label: 'Rating', suggestedUiType: 'checkbox' },
  inStock: { label: 'In Stock', suggestedUiType: 'toggle' },
  vintage: { label: 'Vintage', suggestedUiType: 'checkbox' },
  tags: { label: 'Tags', suggestedUiType: 'checkbox' },
  body: { label: 'Body', suggestedUiType: 'checkbox' },
  acidity: { label: 'Acidity', suggestedUiType: 'checkbox' },
  tannins: { label: 'Tannins', suggestedUiType: 'checkbox' },
  fruitiness: { label: 'Aromatic Intensity', suggestedUiType: 'checkbox' },
};

// Localized labels per field, so the admin shows facet names in the selected
// admin language regardless of what (possibly wrong-language) `name` is stored
// in the config. Stored `name` is only used as a fallback for custom fields.
const FIELD_LABELS_BY_LANG: Record<string, Record<string, string>> = {
  el: {
    category: 'Κατηγορία', type: 'Χρώμα', producer: 'Παραγωγός', region: 'Περιοχή',
    country: 'Χώρα', grape: 'Ποικιλία', price: 'Εύρος Τιμής', rating: 'Βαθμολογία',
    inStock: 'Διαθέσιμα', vintage: 'Χρονολογία', tags: 'Ετικέτες', body: 'Σώμα',
    acidity: 'Οξύτητα', tannins: 'Τανίνες', fruitiness: 'Αρωματική ένταση',
  },
  en: {
    category: 'Category', type: 'Wine Color', producer: 'Brand', region: 'Region',
    country: 'Country', grape: 'Grape Variety', price: 'Price Range', rating: 'Rating',
    inStock: 'In Stock', vintage: 'Vintage', tags: 'Tags', body: 'Body',
    acidity: 'Acidity', tannins: 'Tannins', fruitiness: 'Aromatic Intensity',
  },
};

const UI_TYPE_LABELS: Record<FacetUIType, string> = {
  checkbox: 'Checkbox',
  slider: 'Slider',
  color: 'Color',
  radio: 'Radio',
  toggle: 'Toggle',
};

const DEFAULT_FACETS: FilterFacetConfig[] = [
  { id: 'facet_category', name: 'Category', field: 'category', uiType: 'checkbox', expanded: true, enabled: true, order: 0 },
  { id: 'facet_price', name: 'Price Range', field: 'price', uiType: 'slider', expanded: true, enabled: true, order: 1 },
  { id: 'facet_producer', name: 'Brand', field: 'producer', uiType: 'checkbox', expanded: false, enabled: true, order: 2 },
  { id: 'facet_rating', name: 'Rating', field: 'rating', uiType: 'checkbox', expanded: false, enabled: true, order: 3 },
  { id: 'facet_region', name: 'Region', field: 'region', uiType: 'checkbox', expanded: false, enabled: true, order: 4 },
  { id: 'facet_type', name: 'Color', field: 'type', uiType: 'color', expanded: false, enabled: true, order: 5 },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminCategoriesFiltersPage() {
  const { secret } = useOutletContext<AdminContext>();
  const { lang } = useAdminLang();

  const [facets, setFacets] = useState<FilterFacetConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [catalogFacets, setCatalogFacets] = useState<Facets | null>(null);

  // Main categories (curated sidebar list)
  const [mainCats, setMainCats] = useState<MainCategory[]>([]);
  const [availableCats, setAvailableCats] = useState<Array<{ value: string; count: number }>>([]);
  const [mainLoading, setMainLoading] = useState(true);
  const [mainSaving, setMainSaving] = useState(false);

  // Imported collections + exclusion management (Category Navigation tab)
  const [collections, setCollections] = useState<CollectionCategory[]>([]);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [editingFacet, setEditingFacet] = useState<FilterFacetConfig | null>(null);
  const [formField, setFormField] = useState('');
  const [formName, setFormName] = useState('');
  const [formUiType, setFormUiType] = useState<FacetUIType>('checkbox');
  const [formExpanded, setFormExpanded] = useState(false);

  // Drag state
  const dragIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getFilterFacets(secret, lang);
      const loaded = result.facets;
      setFacets(loaded.length > 0 ? loaded : DEFAULT_FACETS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load filter facets');
    } finally {
      setLoading(false);
    }
  }, [secret, lang]);

  useEffect(() => { load(); }, [load]);

  // Fetch catalog facets to show data availability in field selector
  useEffect(() => {
    getCatalogFacets().then((data) => { if (data) setCatalogFacets(data); });
  }, []);

  // Load imported collections + current exclusions
  useEffect(() => {
    let cancelled = false;
    setCollectionsLoading(true);
    getAdminCategories(secret, lang)
      .then((r) => {
        if (cancelled) return;
        setCollections(r.categories);
        setExcludedIds(r.excludedIds);
      })
      .catch(() => { /* section shows empty state */ })
      .finally(() => { if (!cancelled) setCollectionsLoading(false); });
    return () => { cancelled = true; };
  }, [secret, lang]);

  // Load main categories + everything available in the catalogue
  useEffect(() => {
    let cancelled = false;
    setMainLoading(true);
    getMainCategories(secret, lang)
      .then((r) => {
        if (cancelled) return;
        setMainCats(r.selected);
        setAvailableCats(r.available);
      })
      .catch(() => { /* section shows empty state */ })
      .finally(() => { if (!cancelled) setMainLoading(false); });
    return () => { cancelled = true; };
  }, [secret, lang]);

  const persistMain = useCallback(async (next: MainCategory[]) => {
    const prev = mainCats;
    setMainCats(next);
    setMainSaving(true);
    try {
      await updateMainCategories(secret, next, lang);
    } catch (err) {
      setMainCats(prev); // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to save main categories');
    } finally {
      setMainSaving(false);
    }
  }, [mainCats, secret, lang]);

  const toggleMain = (value: string) => {
    const exists = mainCats.some((m) => m.value === value);
    persistMain(exists ? mainCats.filter((m) => m.value !== value) : [...mainCats, { value }]);
  };

  const moveMain = (idx: number, dir: -1 | 1) => {
    const next = [...mainCats];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    persistMain(next);
  };

  const renameMain = (value: string, label: string) => {
    setMainCats((prev) => prev.map((m) => (m.value === value ? { ...m, label: label || undefined } : m)));
  };

  // Toggle a collection's visibility on public surfaces (optimistic + persist)
  const handleToggleExcluded = useCallback(async (id: string) => {
    const prev = excludedIds;
    const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    setExcludedIds(next);
    try {
      await updateCategoryExclusions(secret, next);
    } catch (err) {
      setExcludedIds(prev); // revert on failure
      setError(err instanceof Error ? err.message : 'Failed to save exclusions');
    }
  }, [excludedIds, secret]);

  // ---------------------------------------------------------------------------
  // Persist
  // ---------------------------------------------------------------------------

  const persist = useCallback(async (updated: FilterFacetConfig[]) => {
    setSaving(true);
    try {
      await updateFilterFacets(secret, updated, lang);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [secret, lang]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggleExpanded = (id: string) => {
    const updated = facets.map((f) =>
      f.id === id ? { ...f, expanded: !f.expanded } : f
    );
    setFacets(updated);
    persist(updated);
  };

  const handleToggleEnabled = (id: string) => {
    const updated = facets.map((f) =>
      f.id === id ? { ...f, enabled: !f.enabled } : f
    );
    setFacets(updated);
    persist(updated);
  };

  const handleDelete = (id: string) => {
    if (!confirm('Remove this filter facet?')) return;
    const updated = facets.filter((f) => f.id !== id);
    // Re-index order
    updated.forEach((f, i) => { f.order = i; });
    setFacets(updated);
    persist(updated);
  };

  const handleOpenAdd = () => {
    setDialogMode('add');
    setEditingFacet(null);
    setFormField('');
    setFormName('');
    setFormUiType('checkbox');
    setFormExpanded(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (facet: FilterFacetConfig) => {
    setDialogMode('edit');
    setEditingFacet(facet);
    setFormField(facet.field);
    setFormName(facet.name);
    setFormUiType(facet.uiType);
    setFormExpanded(facet.expanded);
    setDialogOpen(true);
  };

  const handleFieldChange = (field: string) => {
    setFormField(field);
    const meta = PRODUCT_FIELDS[field];
    if (meta) {
      setFormName(meta.label);
      setFormUiType(meta.suggestedUiType);
    }
  };

  const handleDialogSubmit = () => {
    if (!formField || !formName) return;

    if (dialogMode === 'add') {
      const newFacet: FilterFacetConfig = {
        id: `facet_${formField}_${Date.now()}`,
        name: formName,
        field: formField,
        uiType: formUiType,
        expanded: formExpanded,
        enabled: true,
        order: facets.length,
      };
      const updated = [...facets, newFacet];
      setFacets(updated);
      persist(updated);
    } else if (editingFacet) {
      const updated = facets.map((f) =>
        f.id === editingFacet.id
          ? { ...f, name: formName, field: formField, uiType: formUiType, expanded: formExpanded }
          : f
      );
      setFacets(updated);
      persist(updated);
    }

    setDialogOpen(false);
  };

  // ---------------------------------------------------------------------------
  // Drag-and-drop reorder
  // ---------------------------------------------------------------------------

  const handleDragStart = (idx: number) => {
    dragIdx.current = idx;
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };

  const handleDrop = (idx: number) => {
    const from = dragIdx.current;
    if (from === null || from === idx) {
      dragIdx.current = null;
      setDragOverIdx(null);
      return;
    }
    const items = [...facets];
    const [moved] = items.splice(from, 1);
    items.splice(idx, 0, moved);
    items.forEach((f, i) => { f.order = i; });
    setFacets(items);
    persist(items);
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  const handleDragEnd = () => {
    dragIdx.current = null;
    setDragOverIdx(null);
  };

  // ---------------------------------------------------------------------------
  // Derived
  // ---------------------------------------------------------------------------

  // Map product field names to catalog facet keys
  const FIELD_TO_CATALOG_KEY: Record<string, string> = {
    category: 'categories', subcategory: 'subcategories', type: 'wineTypes',
    country: 'countries', region: 'regions', grape: 'grapes',
    producer: 'producers', vintage: 'vintages', tags: 'tags',
  };

  /** Get number of distinct values for a product field from catalog data */
  const getCatalogCount = (field: string): number => {
    if (field === 'price') return catalogFacets?.priceRange ? 1 : 0;
    if (field === 'inStock') return 1; // always available
    const key = FIELD_TO_CATALOG_KEY[field];
    if (!key || !catalogFacets) return 0;
    const buckets = catalogFacets[key];
    return Array.isArray(buckets) ? buckets.length : 0;
  };

  const usedFields = new Set(facets.map((f) => f.field));
  const availableFields = Object.entries(PRODUCT_FIELDS).filter(
    ([key]) => dialogMode === 'edit' ? true : !usedFields.has(key)
  );

  const optionsCount = (facet: FilterFacetConfig): string => {
    const catalogCount = getCatalogCount(facet.field);
    if (catalogCount > 0) return `${catalogCount} values`;
    if (facet.options && facet.options.length > 0) return `${facet.options.length} options`;
    return '';
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Categories & Filters</h1>
          <p className="text-muted-foreground">Configure category navigation and faceted filters</p>
        </div>
        <Button size="sm" onClick={handleOpenAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Filter Facet
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center justify-between">
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="facets">
        <TabsList>
          <TabsTrigger value="facets">Filter Facets</TabsTrigger>
          <TabsTrigger value="categories">Category Navigation</TabsTrigger>
          <TabsTrigger value="quickjump">Main Categories</TabsTrigger>
        </TabsList>

        {/* ---- Filter Facets Tab ---- */}
        <TabsContent value="facets" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Filter Facets Configuration</CardTitle>
              <CardDescription>
                Define which filters appear in the search experience and how they're displayed
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-16 text-center text-muted-foreground">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading facets...
                </div>
              ) : facets.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground">
                  No filter facets configured. Click "Add Filter Facet" to get started.
                </div>
              ) : (
                <div className="space-y-2">
                  {facets
                    .sort((a, b) => a.order - b.order)
                    .map((facet, idx) => (
                      <div
                        key={facet.id}
                        draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop(idx)}
                        onDragEnd={handleDragEnd}
                        className={`flex items-center gap-3 rounded-lg border bg-card px-4 py-3 transition-colors ${
                          dragOverIdx === idx ? 'border-primary bg-primary/5' : ''
                        } ${!facet.enabled ? 'opacity-50' : ''}`}
                      >
                        {/* Drag handle */}
                        <div className="cursor-grab text-muted-foreground hover:text-foreground">
                          <GripVertical className="h-5 w-5" />
                        </div>

                        {/* Name + badges */}
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="font-semibold text-sm">{FIELD_LABELS_BY_LANG[lang]?.[facet.field] ?? facet.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {UI_TYPE_LABELS[facet.uiType]}
                          </Badge>
                          {optionsCount(facet) && (
                            <Badge variant="secondary" className="text-xs">
                              {optionsCount(facet)}
                            </Badge>
                          )}
                          {catalogFacets && getCatalogCount(facet.field) === 0 && facet.field !== 'price' && facet.field !== 'inStock' && (
                            <Badge variant="destructive" className="text-xs">
                              no data
                            </Badge>
                          )}
                        </div>

                        {/* Expanded toggle */}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
                          <span>Expanded</span>
                          <Switch
                            checked={facet.expanded}
                            onCheckedChange={() => handleToggleExpanded(facet.id)}
                          />
                        </div>

                        {/* Settings button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Settings"
                          onClick={() => handleOpenEdit(facet)}
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                        </Button>

                        {/* Delete button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Delete"
                          onClick={() => handleDelete(facet.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                </div>
              )}
              {saving && (
                <p className="mt-3 text-xs text-muted-foreground">Saving...</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Category Navigation Tab ---- */}
        <TabsContent value="categories" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Imported Collections</CardTitle>
              <CardDescription>
                Collections imported by category feeds. Hidden collections don't appear in
                search results or the discovery Categories section.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {collectionsLoading ? (
                <div className="py-16 text-center text-muted-foreground">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading collections...
                </div>
              ) : collections.length === 0 ? (
                <div className="py-12 text-center text-muted-foreground">
                  No collections imported yet — add a category-type feed in Feed Management.
                </div>
              ) : (
                <>
                  <div className="mb-3 text-sm text-muted-foreground">
                    {collections.length} collections · {excludedIds.length} hidden
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {collections.map((col) => {
                      const hidden = excludedIds.includes(col.id);
                      return (
                        <div
                          key={col.id}
                          className={[
                            'flex items-center gap-3 rounded-lg border p-2.5 transition-opacity',
                            hidden ? 'opacity-50' : '',
                          ].join(' ')}
                        >
                          {col.imageUrl ? (
                            <img
                              src={col.imageUrl}
                              alt=""
                              className="h-10 w-14 rounded object-cover flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-10 w-14 rounded bg-muted flex-shrink-0" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium" title={col.name}>{col.name}</div>
                            {col.description && (
                              <div className="truncate text-xs text-muted-foreground" title={col.description}>
                                {col.description}
                              </div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex-shrink-0"
                            title={hidden ? 'Show in search & discovery' : 'Hide from search & discovery'}
                            onClick={() => handleToggleExcluded(col.id)}
                          >
                            {hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---- Quick Jump Tab ---- */}
        <TabsContent value="quickjump" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Main Categories</CardTitle>
              <CardDescription>
                The categories shown in the search sidebar, in this order. The shop's taxonomy is
                flat — sales and campaign collections sit next to real categories — so pick the
                main ones here. Counts include every product listed under a category.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mainLoading ? (
                <div className="py-16 text-center text-muted-foreground">
                  <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                  Loading categories...
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Selected — ordered */}
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-medium">Shown in sidebar ({mainCats.length})</h3>
                      {mainSaving && <span className="text-xs text-muted-foreground">Saving…</span>}
                    </div>
                    {mainCats.length === 0 ? (
                      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                        None selected — the built-in defaults are used.
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {mainCats.map((m, idx) => {
                          const count = availableCats.find((a) => a.value === m.value)?.count ?? 0;
                          return (
                            <div key={m.value} className="flex items-center gap-2 rounded-lg border p-2">
                              <div className="flex flex-col">
                                <Button variant="ghost" size="icon" className="h-4 w-6" disabled={idx === 0}
                                  onClick={() => moveMain(idx, -1)} title="Move up">
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-4 w-6" disabled={idx === mainCats.length - 1}
                                  onClick={() => moveMain(idx, 1)} title="Move down">
                                  <ArrowDown className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="min-w-0 flex-1">
                                <Input
                                  value={m.label ?? ''}
                                  placeholder={m.value}
                                  onChange={(e) => renameMain(m.value, e.target.value)}
                                  onBlur={() => persistMain(mainCats)}
                                  className="h-8"
                                  title="Display name (leave empty to use the feed's name)"
                                />
                                {m.label && (
                                  <div className="mt-0.5 truncate text-xs text-muted-foreground">
                                    feed: {m.value}
                                  </div>
                                )}
                              </div>
                              <Badge variant="secondary" className="tabular-nums">{count}</Badge>
                              <Button variant="ghost" size="sm" onClick={() => toggleMain(m.value)} title="Remove from sidebar">
                                <EyeOff className="h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Everything else in the catalogue */}
                  <div>
                    <h3 className="mb-2 text-sm font-medium">
                      All categories in the catalogue ({availableCats.length})
                    </h3>
                    <div className="max-h-[28rem] space-y-1 overflow-y-auto pr-1">
                      {availableCats
                        .filter((a) => !mainCats.some((m) => m.value === a.value))
                        .map((a) => (
                          <div key={a.value} className="flex items-center gap-2 rounded-lg border p-2 opacity-70">
                            <span className="min-w-0 flex-1 truncate text-sm" title={a.value}>{a.value}</span>
                            <Badge variant="outline" className="tabular-nums">{a.count}</Badge>
                            <Button variant="ghost" size="sm" onClick={() => toggleMain(a.value)} title="Add to sidebar">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---- Add / Edit Dialog ---- */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'add' ? 'Add Filter Facet' : 'Edit Filter Facet'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'add'
                ? 'Choose a product field and configure how it appears as a filter.'
                : 'Update the filter facet settings.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="facet-field">Product Field</Label>
              <Select
                value={formField}
                onValueChange={handleFieldChange}
                disabled={dialogMode === 'edit'}
              >
                <SelectTrigger id="facet-field">
                  <SelectValue placeholder="Select a field..." />
                </SelectTrigger>
                <SelectContent>
                  {availableFields.map(([key, meta]) => {
                    const count = getCatalogCount(key);
                    return (
                      <SelectItem key={key} value={key}>
                        {meta.label}
                        {count > 0
                          ? ` (${count} values)`
                          : ' (no data)'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="facet-name">Display Name</Label>
              <Input
                id="facet-name"
                placeholder="e.g. Category, Brand, Price Range"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="facet-uitype">Filter Type</Label>
              <Select
                value={formUiType}
                onValueChange={(v) => setFormUiType(v as FacetUIType)}
              >
                <SelectTrigger id="facet-uitype">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(UI_TYPE_LABELS) as [FacetUIType, string][]).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="facet-expanded">Expanded by default</Label>
              <Switch
                id="facet-expanded"
                checked={formExpanded}
                onCheckedChange={setFormExpanded}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDialogSubmit} disabled={!formField || !formName}>
              {dialogMode === 'add' ? 'Add Facet' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
